<template>
  <div class="enter-code">
    <div v-if="signupText" class="ri-body margin-bottom-xxs">{{ signupText }}</div>
    <div class="ri-large-headline ri-alte margin-bottom-xxs">מה הקוד החד פעמי שקיבלת?</div>
    <div class="ri-large-body">
      <div v-if="sendCodeViaSMS">
        <div>שלחנו לך קוד למספר {{ formattedPhone }}</div>
      </div>
      <div v-else>
        <div>שלחנו לך קוד {{ otpSentVia }} למספר <div>{{ formattedPhone }}</div></div>
      </div>
    </div>
    <div class="wrong-number-link" v-on:click="goBack">זה לא המספר הנכון</div>

    <text-input
      class="otp-input"
      :autofocus="true"
      label-text="קוד 6 ספרות"
      :value="code"
      :onInput="onInput"
      :error="error"
      :inputPattern="'\\d*'"
      :error-message="errorLabel"
      inputType="tel"
      autocomplete="one-time-code" />

    <riseup-button
      :title="buttonTitle"
      @click="() => confirmCode()"
      :loading="loading"
      :disabled="buttonDisabled"
      :size="'small'"
      class="otp-submit"/>

    <template v-if="!sendAgainLoading">
      <resend-code-option
        text="לא קיבלתי את הקוד, אפשר לשלוח לי שוב?"
        :icon="require('@/assets/icons/refresh-icon.svg')"
        @click="() => sendCodeAgain()"
      />
      <resend-code-option
        v-if="sendCodeViaSMS"
        text="אני רוצה לקבל את הקוד בהודעת ווטסאפ"
        :icon="require('@/assets/icons/whatsapp-icon-v2.svg')"
        @click="() => sendCodeAgain('whatsapp')"
      />
      <resend-code-option
        v-if="voiceOtpEnabled"
        text="אני רוצה לקבל את הקוד בשיחה טלפונית"
        :icon="require('@/assets/icons/phone-icon.svg')"
        @click="() => sendCodeAgain('voice')"
      />
    </template>
    <div class="loader" v-if="sendAgainLoading"/>

  </div>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import phoneNumberUtils from '@/utils/phoneNumber';
import OtpMixin from '@/mixins/OtpMixin.vue';
import DDLogs from '@/DDLogs';
import Segment from '../Segment';
import ResendCodeOption from './ResendCodeOption.vue';

const ERRORS = {
  CODE_EXPIRED: 'CODE_EXPIRED',
  INVALID_CODE: 'INVALID_CODE',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

export default {
  name: 'EnterCode',
  mixins: [OtpMixin],
  components: {
    RiseupButton: BaseUI.V2Components.RiseupButton,
    TextInput: BaseUI.V2Components.TextInput,
    ResendCodeOption,
  },
  props: {
    signupText: {
      type: String,
      required: false,
    },
    phoneNumber: {
      type: String,
      required: true,
    },
    confirmCodeAction: {
      type: Function,
      required: true,
    },
    regenerateCodeAction: {
      type: Function,
      required: true,
    },
    serverError: {
      type: String,
      required: true,
    },
    resetError: {
      type: Function,
      required: true,
    },
    buttonTitle: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      code: '',
      sendAgainLoading: false,
      triedAutoConfirm: false,
      abortController: new AbortController(),
    };
  },
  created() {
    if (this.sendCodeViaSMS && 'OTPCredential' in window) {
      navigator.credentials.get({
        otp: { transport: ['sms'] },
        signal: this.abortController.signal,
      }).then(otp => {
        this.code = otp.code;
        this.confirmCode();
      }).catch(err => {
        DDLogs.log('Failed autofilling otp', err);
      });
    }
  },
  computed: {
    buttonDisabled() {
      return this.code.length !== 6 || this.loading;
    },
    codeContainsOnlyNumbers() {
      return /^([0-9]*)$/.test(this.code);
    },
    error() {
      return !!this.serverError || !this.codeContainsOnlyNumbers;
    },
    errorLabel() {
      if (this.serverError === ERRORS.CODE_EXPIRED) {
        Segment.trackUserGot('expiredCodeError');
        return 'הקוד פג תוקף';
      } if (this.serverError === ERRORS.INVALID_CODE) {
        Segment.trackUserGot('invalidCodeError', { invalidCode: this.code });
        return 'הקוד שהזנת לא תקין';
      } if (!this.codeContainsOnlyNumbers) {
        return 'הקוד צריך להכיל ספרות בלבד';
      } if (this.serverError === ERRORS.UNKNOWN_ERROR) {
        return 'נראה שקרתה תקלה, נסו שוב בהמשך';
      }
      return null;
    },
    formattedPhone() {
      return phoneNumberUtils.visualizePhoneNumber(this.phoneNumber);
    },
  },
  methods: {
    onInput(value) {
      this.code = value.trim();
      this.resetError();
      if (!this.triedAutoConfirm && this.code.length === 6) {
        this.triedAutoConfirm = true;
        this.confirmCode();
      }
    },
    async sendCodeAgain(overridePlatform) {
      this.sendAgainLoading = true;
      try {
        const sendPlatform = typeof overridePlatform === 'string' ? overridePlatform : this.sendPlatform;
        Segment.trackUserInteraction('GenerateCodeAgainClicked', { sendPlatform });
        await this.regenerateCodeAction(sendPlatform);
      } finally {
        this.sendAgainLoading = false;
      }
    },
    async confirmCode() {
      this.abortController.abort();
      Segment.trackUserInteraction('buttonClicked', {
        componentName: 'EnterCode',
        buttonText: 'אישור',
      });
      this.confirmCodeAction(this.code);
    },
    goBack() {
      Segment.trackUserInteraction('buttonClicked', {
        componentName: 'EnterCode',
        buttonText: 'זה לא המספר הנכון',
      });
      this.$router.back();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~@riseupil/base-ui/src/scss/riseup-colors';
  @import '~@riseupil/base-ui/src/scss/spacings';
  @import '~@riseupil/base-ui/src/scss/typography';
  @import '../scss/mixins';

  .enter-code {

    .notice {
      color: $riseup_red;
    }

    .otp-input {
      margin-bottom: 40px;
    }

    .riseup-button {
      width: 100%;
      margin: 0 0 40px;
    }

    ::v-deep .riseup-button + .resend-option {
      margin-top: 0;
    }

    .wrong-number-link {
      display: inline-block;
      margin-top: $ri-spacing-s;
      margin-bottom: 40px;
      color: $riseup_blue;
      font-size: 18px;
      text-decoration: underline;
      cursor: pointer;

      &:active {
        color: $riseup_dark_blue;
      }
    }

    .loader {
      margin: 0 auto;
      width: 34px;
      height: 34px;
      border: 4px solid $riseup_blue;
      border-top: 4px solid $riseup_white;
      border-radius: 50%;
      animation: spin 2s linear infinite;

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    }
  }
</style>
